import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Shield as RulesIcon, Ban as BlockIcon, Filter as FilterIcon, LayoutGrid as AppIcon } from 'lucide-react'
import { useOnboarding } from './OnboardingFlow'
import { polling } from '../../lib/polling'
import { api } from '../../lib/api'

export default function RulesStep() {
  const navigate = useNavigate()
  const { nextStep, validationError } = useOnboarding()
  const [hasDevices, setHasDevices] = useState(false)
  const [hasPolicies, setHasPolicies] = useState(false)
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    const checkRequirements = async () => {
      try {
        const prerequisites = await api.getOnboardingPrerequisites()
        setHasDevices(prerequisites.hasDevices)
        setHasPolicies(prerequisites.hasPolicies)
      } catch {
        setHasDevices(false)
        setHasPolicies(false)
      } finally {
        setChecking(false)
      }
    }

    checkRequirements()

    const interval = setInterval(checkRequirements, polling.onboardingCheck)
    return () => clearInterval(interval)
  }, [])

  const handleCreateRule = () => {
    navigate({ to: '/rules/new' })
  }

  const handleContinue = async () => {
    await nextStep()
  }

  const ruleTypes = [
    { icon: <BlockIcon className="text-red-500" />, title: 'Website Blocking', description: 'Block specific websites or categories' },
    { icon: <FilterIcon className="text-amber-500" />, title: 'Content Filtering', description: 'Filter inappropriate content automatically' },
    { icon: <AppIcon className="text-blue-500" />, title: 'App Restrictions', description: 'Control which apps can be used' },
  ]

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12">
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-2xl bg-brand-light flex items-center justify-center mx-auto mb-6">
          <RulesIcon size={64} className="text-brand" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Set Up Rules</h2>
        <p className="text-gray-600 max-w-md mx-auto">
          Create rules to protect your family from inappropriate content and manage screen time.
        </p>
      </div>

      {validationError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-700 text-center">{validationError}</p>
        </div>
      )}

      {!checking && !hasDevices && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-800 text-center">
            Please add at least one device before creating rules.
          </p>
        </div>
      )}

      {!checking && hasPolicies && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm text-green-800 text-center">
            Great! You have a rule. Click continue to proceed.
          </p>
        </div>
      )}

      <div className="space-y-3 mb-8">
        {ruleTypes.map((rule, index) => (
          <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
              {rule.icon}
            </div>
            <div>
              <h4 className="font-medium text-gray-900">{rule.title}</h4>
              <p className="text-sm text-gray-500">{rule.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <button
          onClick={handleCreateRule}
          disabled={!hasDevices}
          className="w-full py-3 bg-brand text-white rounded-xl font-medium hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Create Rule Now
        </button>

        {hasPolicies && (
          <button
            onClick={handleContinue}
            className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            Continue
          </button>
        )}
      </div>
    </div>
  )
}
